import { Capacitor } from "@capacitor/core"
import { AnimatePresence, motion } from "motion/react"
import { ArrowLeftIcon, KeyRoundIcon, MailIcon } from "lucide-react"
import { type FormEvent, useState } from "react"
import { toast } from "sonner"
import { authClient } from "~/lib/auth-client"
import {
	getNativeAuthCallbackUrl,
	getNativeAuthStartUrl,
	getWebAuthProxyStartUrl,
	getWebAuthUrls
} from "~/lib/client-url"
import { api, RouterOutputs } from "~/trpc/react"
import { setPendingNativeAuth } from "~/lib/native-auth-token"
import { BuapeIcon, DiscordIcon, GitHubIcon, GoogleIcon } from "./icons/logos"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Input } from "./ui/input"

export function LoginPanel({
	callbackURL = "/",
	enabled,
	organizationSlug: initialOrganizationSlug
}: {
	callbackURL?: string
	enabled?: Partial<RouterOutputs["meta"]["authProviders"]>
	organizationSlug?: string
}) {
	const authProvidersQuery = api.meta.authProviders.useQuery(undefined, {
		enabled: enabled === undefined
	})
	const availableProviders = enabled ?? authProvidersQuery.data
	const [email, setEmail] = useState("")
	const [organizationSlug, setOrganizationSlug] = useState(
		initialOrganizationSlug ?? ""
	)
	const [password, setPassword] = useState("")
	const [passwordPending, setPasswordPending] = useState(false)
	const [providerPending, setProviderPending] = useState<number | null>(null)
	const [loginMode, setLoginMode] = useState<"providers" | "email" | "sso">(
		"providers"
	)
	const resolveOidcProvider = api.meta.resolveOidcProvider.useMutation()
	const nativeApp = Capacitor.isNativePlatform()
	const webAuthUrls = getWebAuthUrls(callbackURL)
	const fullCallbackURL = webAuthUrls.callbackURL
	const errorCallbackURL = webAuthUrls.errorCallbackURL
	// Each native login attempt arms the one-time pending state the app
	// requires before accepting an auth deep link; both callback URLs echo it.
	const startNativeLogin = () => {
		const authState = setPendingNativeAuth()
		return {
			callbackURL: getNativeAuthCallbackUrl(
				webAuthUrls.callbackURL,
				authState ?? undefined
			),
			errorCallbackURL: getNativeAuthCallbackUrl(
				"/unauthorized",
				authState ?? undefined
			)
		}
	}
	const openNativeAuth = async (provider: string, email?: string) => {
		const { Browser } = await import("@capacitor/browser")
		const nativeLogin = startNativeLogin()
		await Browser.open({
			url: getNativeAuthStartUrl({
				provider,
				callbackURL: nativeLogin.callbackURL,
				errorCallbackURL: nativeLogin.errorCallbackURL,
				email
			}),
			presentationStyle: "fullscreen",
			toolbarColor: "#191a22"
		})
	}
	const openWebAuthProxy = (provider: string, email?: string) => {
		const proxyUrl = getWebAuthProxyStartUrl({
			provider,
			callbackURL: fullCallbackURL,
			errorCallbackURL,
			email
		})
		if (!proxyUrl) return false
		window.location.assign(proxyUrl)
		return true
	}
	const handlePasswordLogin = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setPasswordPending(true)
		try {
			const result = await authClient.signIn.email({
				email,
				password,
				callbackURL: nativeApp
					? startNativeLogin().callbackURL
					: fullCallbackURL,
				rememberMe: true
			})
			if (result.error) {
				toast.error(result.error.message || "Email login failed")
			}
		} catch (error) {
			console.error("Email login failed:", error)
			toast.error("Email login failed")
		} finally {
			setPasswordPending(false)
		}
	}
	const handleDiscordLogin = async () => {
		try {
			if (nativeApp) {
				await openNativeAuth("discord")
				return
			}
			if (openWebAuthProxy("discord")) return
			await authClient.signIn.social({
				provider: "discord",
				callbackURL: fullCallbackURL,
				errorCallbackURL,
				disableRedirect: false
			})
		} catch (error) {
			console.error("Discord login failed:", error)
			window.location.href = "/unauthorized"
		}
	}

	const handleGoogleLogin = async () => {
		try {
			if (nativeApp) {
				await openNativeAuth("google")
				return
			}
			if (openWebAuthProxy("google")) return
			await authClient.signIn.social({
				provider: "google",
				callbackURL: fullCallbackURL,
				errorCallbackURL,
				disableRedirect: false
			})
		} catch (error) {
			console.error("Google login failed:", error)
			window.location.href = "/unauthorized"
		}
	}

	const handleGitHubLogin = async () => {
		try {
			if (nativeApp) {
				await openNativeAuth("github")
				return
			}
			if (openWebAuthProxy("github")) return
			await authClient.signIn.social({
				provider: "github",
				callbackURL: fullCallbackURL,
				errorCallbackURL,
				disableRedirect: false
			})
		} catch (error) {
			console.error("GitHub login failed:", error)
			window.location.href = "/unauthorized"
		}
	}

	const handleBuapeLogin = async () => {
		try {
			if (nativeApp) {
				await openNativeAuth("buape")
				return
			}
			if (openWebAuthProxy("buape")) return
			await authClient.signIn.sso({
				providerId: "buape",
				callbackURL: fullCallbackURL,
				errorCallbackURL
			})
		} catch (error) {
			console.error("Buape login failed", error)
			window.location.href = "/unauthorized"
		}
	}

	const handleOidcLogin = async () => {
		const slug = organizationSlug.trim()
		if (!slug) {
			toast.error("Enter your organization slug first")
			return
		}
		try {
			const inviteId = new URL(
				callbackURL,
				window.location.origin
			).pathname.match(/^\/invite\/([^/]+)$/)?.[1]
			const { providerId } = await resolveOidcProvider.mutateAsync({
				organizationSlug: slug,
				inviteId
			})
			if (nativeApp) {
				await openNativeAuth(providerId)
				return
			}
			if (openWebAuthProxy(providerId)) return
			await authClient.signIn.sso({
				providerId,
				callbackURL: fullCallbackURL,
				errorCallbackURL
			})
		} catch (error) {
			console.error("OIDC login failed", error)
			toast.error(error instanceof Error ? error.message : "SSO login failed")
		}
	}

	const lastMethod = authClient.getLastUsedLoginMethod()
	const buttons = []

	if (availableProviders?.discord) {
		buttons.push({
			onClick: handleDiscordLogin,
			icon: <DiscordIcon fill="white" />,
			children: "Login with Discord",
			badgeText: lastMethod === "discord" ? "Last used" : undefined
		})
	}

	if (availableProviders?.google) {
		buttons.push({
			onClick: handleGoogleLogin,
			icon: <GoogleIcon className="size-4" />,
			children: "Login with Google",
			badgeText: lastMethod === "google" ? "Last used" : undefined
		})
	}

	if (availableProviders?.github) {
		buttons.push({
			onClick: handleGitHubLogin,
			icon: <GitHubIcon fill="white" />,
			children: "Login with GitHub",
			badgeText: lastMethod === "github" ? "Last used" : undefined
		})
	}

	if (availableProviders?.buape) {
		buttons.push({
			onClick: handleBuapeLogin,
			icon: <BuapeIcon />,
			children: "Login with Buape",
			badgeText: lastMethod === "buape" ? "Last used" : undefined
		})
	}

	if (availableProviders?.sso) {
		buttons.push({
			onClick: () => setLoginMode("sso"),
			icon: <KeyRoundIcon className="size-4" />,
			children: "Login with SSO",
			badgeText: lastMethod === "oidc" ? "Last used" : undefined
		})
	}

	return (
		<div className="bg-background-secondary flex min-h-72 w-full flex-col items-center justify-center gap-4 rounded-md p-4 sm:p-8">
			<motion.form
				onSubmit={(event) => {
					if (loginMode === "sso") {
						event.preventDefault()
						void handleOidcLogin()
						return
					}
					void handlePasswordLogin(event)
				}}
				className="flex w-full flex-col gap-4"
			>
				<AnimatePresence mode="wait">
					{loginMode !== "providers" ? (
						<motion.div
							key={`${loginMode}-fields`}
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: 100 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							className="flex flex-col gap-4"
							style={{ order: 0 }}
						>
							<Button
								type="button"
								variant="ghost"
								className="w-fit"
								onClick={() => setLoginMode("providers")}
							>
								<ArrowLeftIcon className="size-4" />
								Back to login options
							</Button>
							<Input
								type={loginMode === "sso" ? "text" : "email"}
								value={loginMode === "sso" ? organizationSlug : email}
								onChange={(event) =>
									loginMode === "sso"
										? setOrganizationSlug(event.target.value)
										: setEmail(event.target.value)
								}
								placeholder={
									loginMode === "sso" ? "Organization slug" : "Email"
								}
								autoComplete={loginMode === "sso" ? "organization" : "email"}
								required
							/>
							{loginMode === "email" && availableProviders?.password && (
								<Input
									type="password"
									value={password}
									onChange={(event) => setPassword(event.target.value)}
									placeholder="Password"
									autoComplete="current-password"
									required
								/>
							)}
							{loginMode === "sso" && availableProviders?.sso && (
								<Button
									type="submit"
									disabled={resolveOidcProvider.isPending}
									className="w-full"
									shape="rounded"
								>
									<KeyRoundIcon className="size-4" />
									Login with SSO
								</Button>
							)}
						</motion.div>
					) : (
						<motion.div
							key="provider-options"
							initial={{ opacity: 0, y: 0 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: 100 }}
							transition={{ duration: 0.12, ease: "easeOut" }}
							className="flex flex-col gap-4"
							style={{ order: 1 }}
						>
							{!availableProviders && authProvidersQuery.isError ? (
								<div className="flex flex-col items-center gap-3" role="alert">
									<p className="text-sm text-text-sub">Unable to reach API</p>
									<Button
										type="button"
										variant="outline"
										onClick={() => void authProvidersQuery.refetch()}
										disabled={authProvidersQuery.isFetching}
										pending={authProvidersQuery.isFetching}
										icon={null}
									>
										Retry
									</Button>
								</div>
							) : availableProviders && buttons.length === 0 ? (
								<p className="text-sm text-text-sub">
									No login methods are available.
								</p>
							) : (
								buttons.map((button, index) => (
									<div key={index} className="relative w-full">
										<Button
											type="button"
											onClick={async () => {
												setProviderPending(index)
												try {
													await button.onClick()
												} finally {
													setProviderPending(null)
												}
											}}
											className="w-full"
											disabled={providerPending !== null}
											shape="rounded"
											pending={providerPending === index}
											icon={button.icon}
										>
											{button.children}
										</Button>
										{button.badgeText && (
											<Badge className="absolute -top-2 -right-2 z-10">
												{button.badgeText}
											</Badge>
										)}
									</div>
								))
							)}
						</motion.div>
					)}
				</AnimatePresence>
				<motion.div
					layout
					transition={{ duration: 0.25, ease: "easeOut" }}
					style={{ order: loginMode === "providers" ? 0 : 1 }}
				>
					{availableProviders?.password && loginMode !== "sso" && (
						<Button
							type={loginMode === "email" ? "submit" : "button"}
							onClick={(event) => {
								if (loginMode !== "email") {
									event.preventDefault()
									setLoginMode("email")
								}
							}}
							disabled={passwordPending}
							className="w-full"
							pending={passwordPending}
							icon={<MailIcon className="size-4" />}
						>
							Login with email
						</Button>
					)}
				</motion.div>
			</motion.form>
		</div>
	)
}
